import React, { useEffect, useMemo, useState } from 'react';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import Loader from '../common/Loader/Loader';
import HeaderElements from '../navigation/components/HeaderElements';
import WellSummaryWidget from '../gl-dashboard/components/WellSummaryWidget';
import OptimizationSummary from '../gl-dashboard/components/OptimizationSummary';
import 'react-grid-layout/css/styles.css';
import './ESPDashboard.scss';
import ESPTableWidget from '../esp-dashboard/TableData/ESPTableWidget';
import RecentTestsData from './widgets/RecentTestData';
import BarChart from './components/BarChart';
import ParetoProduction from '../gl-dashboard/components/ParetoProduction';

//Filter
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { handleRemoveFilter } from '../well-filters/wellFilterUtils';

import {
  frequencyRateTableColumns,
  pumpChangeTableColumns,
  producerTop5Columns,
  producerBottom5Columns,
} from './TableData/ESPTableData';

import {
  fetchESPDashboard,
  selectEspDashboardLoading,
  selectEspDashboardError,
  selectEspFrequencyChange,
  selectEspPumpChange,
  selectEspProductionTop5,
  selectEspProductionBottom5,
  selectEspRecentTests,
  selectEspsInRange,
  selectDegradationSummary,
  selectDrawdownSummary,
  selectOptimizationOpportunities,
  selectESPChartData,
  selectESPParetoCounts,
  selectESPSummaryCount,
  selectEspOptimizationData,
  selectESPDegradationWellsAffected,
} from './espDashboardSlice';
import OpportunitiesChart from '../recommendations/Opportunities/OpportunitiesChart';
import MenuNavIcon from '../navigation/components/NavMenuIcon';

const GridLayout = WidthProvider(RGL);

interface WidgetData {
  key: string;
  layout: {
    lg: {
      x: number;
      y: number;
      width: number;
      height: number;
      minH?: number;
      minW?: number;
    };
  };
}

/** Placeholder widget used for panels not implemented in this sprint */
const PlaceholderWidget: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <div className='placeholder-widget h-full w-full bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center p-6'>
    <div className='text-center'>
      <div className='widget-icon w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-lg mx-auto mb-4 flex items-center justify-center' />
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2'>{title}</h3>
      {description && <p className='text-sm text-gray-600 dark:text-gray-400'>{description}</p>}
    </div>
  </div>
);

const defaultWidgetLayout: WidgetData[] = [
  { key: 'Overall Summary', layout: { lg: { x: 0, y: 0, width: 12, height: 3.5 } } },
  { key: 'Optimization Summary', layout: { lg: { x: 0, y: 4, width: 6, height: 11.5 } } },
  { key: 'Pareto Production', layout: { lg: { x: 7, y: 4, width: 6, height: 11.5 } } },
  { key: 'Recent Tests', layout: { lg: { x: 0, y: 15, width: 3, height: 8 } } },
  { key: 'Pumps in Range', layout: { lg: { x: 3, y: 15, width: 3, height: 8 } } },
  { key: 'Pumps with Degradation', layout: { lg: { x: 6, y: 15, width: 3, height: 8 } } },
  { key: 'Wells Pumped Off', layout: { lg: { x: 9, y: 15, width: 3, height: 8 } } },
  { key: 'Optimization Opportunities', layout: { lg: { x: 0, y: 24, width: 12, height: 10 } } },

  // ESP recommendation tables
  { key: 'Top 5 Frequency Change Opportunities', layout: { lg: { x: 0, y: 35, width: 12, height: 10 } } },
  { key: 'Top 5 Pump Change Opportunities', layout: { lg: { x: 0, y: 46, width: 12, height: 10 } } },
  { key: 'Top 5 Producers', layout: { lg: { x: 0, y: 57, width: 12, height: 10 } } },
  { key: 'Bottom 5 Producers', layout: { lg: { x: 0, y: 68, width: 12, height: 10 } } },
];

const ESPDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const dispatch = useAppDispatch();

  const sliceLoading = useAppSelector(selectEspDashboardLoading);
  const sliceError = useAppSelector(selectEspDashboardError);

  // raw data from slice
  const frequencyChangeData = useAppSelector(selectEspFrequencyChange);
  const pumpChangeData = useAppSelector(selectEspPumpChange);
  const productionTop5Data = useAppSelector(selectEspProductionTop5);
  const productionBottom5Data = useAppSelector(selectEspProductionBottom5);
  const recentTestsData = useAppSelector(selectEspRecentTests);
  const espsInRangeData = useAppSelector(selectEspsInRange);
  const degradationSummaryData = useAppSelector(selectDegradationSummary);
  const drawdownSummaryData = useAppSelector(selectDrawdownSummary);
  const optimizationData = useAppSelector(selectEspOptimizationData);
  const optimizationOpportunitiesData = useAppSelector(selectOptimizationOpportunities);
  const summaryCounts = useAppSelector(selectESPSummaryCount);
  const paretoCounts = useAppSelector(selectESPParetoCounts);
  const paretoChartData = useAppSelector(selectESPChartData);
  const espDegradationWellsAffected = useAppSelector(selectESPDegradationWellsAffected);
  const selectedFilters = useAppSelector((state: any) => state?.WellFiltersSlice?.PCSelectedFilters ?? []);
  const pcWellIds = useAppSelector((state) => state.WellFiltersSlice.PCWellIds);
  const assets = useAppSelector((state) => state.assetGroups.assets) ?? [];

  const handleRemoveFilterUtil = (filterToRemove: any) => {
    handleRemoveFilter(filterToRemove, selectedFilters, dispatch);
  };

  // numeric helper
  const toNumber = (v: unknown) => {
    const n = Number(v as any);
    return Number.isFinite(n) ? n : 0;
  };

  // Convert EspsInRangeData to chart format
  const chartData = useMemo(() => {
    if (!espsInRangeData || typeof espsInRangeData !== 'object') return [];
    const obj = espsInRangeData;
    return [
      { category: 'Wells in downthrust', value: toNumber(obj.WellsInDownthrust), color: '#0094BD' },
      { category: 'Wells in range', value: toNumber(obj.WellsInRange), color: '#0094BD' },
      { category: 'Wells in upthrust', value: toNumber(obj.WellsInUpthrust), color: '#0094BD' },
    ];
  }, [espsInRangeData]);

  const degradationSummaryChartData = useMemo(() => {
    if (!degradationSummaryData || typeof degradationSummaryData !== 'object') {
      return [];
    }

    return [
      {
        category: '>75%',
        value: degradationSummaryData.WellsDegradedOver75 || 0,
        color: '#0094BD', // Red-500
      },
      {
        category: '50-75%',
        value: degradationSummaryData.WellsDegradedOver50 || 0,
        color: '#0094BD', // Orange-500
      },
      {
        category: '25-50%',
        value: degradationSummaryData.WellsDegradedLess50 || 0,
        color: '#0094BD', // Amber-500
      },
      {
        category: '<25%',
        value: degradationSummaryData.WellsDegradedLess25 || 0,
        color: '#0094BD', // Green-500
      },
    ];
  }, [degradationSummaryData]);

  const drawdownSummaryChartData = useMemo(() => {
    if (!drawdownSummaryData || typeof drawdownSummaryData !== 'object') return [];
    const obj = drawdownSummaryData;
    return [
      { category: '>75%', value: toNumber(obj.WellsDrawdownOver75), color: '#0094BD' },
      { category: '50-75%', value: toNumber(obj.WellsDrawdownOver50), color: '#0094BD' },
      { category: '25-50%', value: toNumber(obj.WellsDrawdownLess50), color: '#0094BD' },
      { category: '<25%', value: toNumber(obj.WellsDrawdownLess25), color: '#0094BD' },
    ];
  }, [drawdownSummaryData]);

  // Common x-axis label styling for both charts
  const xAxisLabelStyle = {
    fontSize: 12,
    color: '#F7F9F9',
    fontFamily: 'Mulish',
    fontWeight: '400' as const,
    lineHeight: 1.5, // 150% as a ratio (18px/12px = 1.5)
  };

  const pumpsInRangeXAxisLabelStyle = {
    fontSize: 8,
    color: '#F7F9F9',
    fontFamily: 'Mulish',
    fontWeight: '400' as const,
    lineHeight: 1.5, // 150% as a ratio (18px/12px = 1.5)
  };

  const getFilterIds = (section: string) =>
    selectedFilters
      .filter((f: any) => f.section === section && typeof f.id === 'number')
      .map((f: any) => f.id)
      .join(',');

  useEffect(() => {
    const load = async () => {
      try {
        const areaIds = getFilterIds('Area');
        const businessUnitIds = getFilterIds('Business unit');
        const fieldIds = getFilterIds('Field');
        await dispatch(
          fetchESPDashboard({
            areaIds: areaIds !== '' ? areaIds : undefined,
            businessUnitIds: businessUnitIds !== '' ? businessUnitIds : undefined,
            fieldIds: fieldIds !== '' ? fieldIds : undefined,
            wellIds: pcWellIds || undefined,
          }),
        );
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch ESP dashboard:', err);
        setErrorMessage('Failed to load ESP dashboard');
        setIsLoading(false);
      }
    };
    load();
  }, [dispatch, selectedFilters, pcWellIds]);

  const safeLayout = (widgetData: WidgetData[]): Layout[] =>
    widgetData.map((obj) => ({
      i: obj.key,
      x: obj.layout.lg.x,
      y: obj.layout.lg.y,
      w: obj.layout.lg.width,
      h: obj.layout.lg.height,
      minH: obj.layout.lg.minH ?? 6,
      minW: obj.layout.lg.minW ?? 3,
    }));

  const layoutMemo = useMemo(() => safeLayout(defaultWidgetLayout), []);

  const renderWidgetContent = (widgetKey: string) => {
    switch (widgetKey) {
      case 'Optimization Summary': {
        const hasData = Array.isArray(optimizationData) && optimizationData.length > 0;

        if (!hasData) {
          return (
            <BarChart
              title='Optimization summary'
              subtitle='No data available to display in bar chart'
              xAxisLabel=''
              data={[]} // empty dataset
              tooltipFormat=''
              showActionButtons={false}
            />
          );
        }

        return <OptimizationSummary data={optimizationData} />;
      }
      case 'Pareto Production':
        return <ParetoProduction count={paretoCounts} chartData={paretoChartData} />;

      case 'Recent Tests':
        return <RecentTestsData recentTestsData={recentTestsData} />;

      case 'Pumps in Range': {
        const source = chartData;
        return (
          <BarChart
            title='Pumps in range'
            subtitle='ESPs in range'
            xAxisLabel='Well count'
            data={source}
            tooltipFormat='{categoryX}: {valueY}'
            showActionButtons={true}
            xAxisLabelStyle={pumpsInRangeXAxisLabelStyle}
            margin='6px 4px 6px 0px'
          />
        );
      }

      case 'Pumps with Degradation': {
        const source = degradationSummaryChartData;
        return (
          <BarChart
            title='Pumps with degradation'
            subtitle='Degradation summary'
            xAxisLabel='% degradation'
            data={source}
            tooltipFormat='{categoryX}: {valueY}'
            showActionButtons={true}
            xAxisLabelStyle={xAxisLabelStyle}
            yAxisLabel='Well count'
            margin='6px 0px 6px 4px'
          />
        );
      }

      case 'Wells Pumped Off': {
        const source = drawdownSummaryChartData;
        return (
          <BarChart
            title='Wells pumped off'
            subtitle='Low pump intake pressure'
            xAxisLabel='% drawdown'
            data={source}
            tooltipFormat='{categoryX}: {valueY}'
            showActionButtons={true}
            xAxisLabelStyle={xAxisLabelStyle}
            yAxisLabel='Well count'
          />
        );
      }

      case 'Overall Summary':
        return (
          <WellSummaryWidget
            count={summaryCounts}
            isEspDashboard={true}
            espDegradationWellsAffected={espDegradationWellsAffected}
          />
        );

      case 'Optimization Opportunities':
        return (
          <OpportunitiesChart
            header='Optimization opportunities'
            showTabs={false}
            data={optimizationOpportunitiesData ?? []}
            fullScreen={true}
          />
        );

      case 'Top 5 Frequency Change Opportunities':
        return (
          <ESPTableWidget
            widgetHeader='Top 5 frequency change opportunities'
            columnData={frequencyRateTableColumns(assets)}
            tableData={frequencyChangeData}
          />
        );

      case 'Top 5 Pump Change Opportunities':
        return (
          <ESPTableWidget
            widgetHeader='Top 5 pump change opportunities'
            columnData={pumpChangeTableColumns(assets)}
            tableData={pumpChangeData}
          />
        );

      case 'Top 5 Producers':
        return (
          <ESPTableWidget
            widgetHeader='Top 5 producers'
            columnData={producerTop5Columns(assets)}
            tableData={productionTop5Data}
          />
        );

      case 'Bottom 5 Producers':
        return (
          <ESPTableWidget
            widgetHeader='Bottom 5 producers'
            columnData={producerBottom5Columns(assets)}
            tableData={productionBottom5Data}
          />
        );

      default:
        return <PlaceholderWidget title='Unknown widget' description='Widget not implemented' />;
    }
  };

  if (isLoading) {
    return <Loader />;
  }
  // determine if there are selected filters so SCSS can add spacing if needed
  const hasSelectedFilters = Array.isArray(selectedFilters) && selectedFilters.length > 0;

  return (
    <ToastServiceProvider>
      <div className={`summary-dashboard ${hasSelectedFilters ? 'has-selected-filters' : ''}`}>
        {(errorMessage || sliceError) && (
          <div className='error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
            {errorMessage || sliceError}
          </div>
        )}

        <div className='summary-header-container'>
          <div className='group-status-top-row'>
            <div className='nav-menu-icon-header-container'>
              <MenuNavIcon />
              <span className='group-status-group-name'>ESP dashboard</span>
            </div>
            <div className='custom-views-header-elements'>
              <HeaderElements showWellsTab showFilterIcon />
            </div>
          </div>
        </div>

        <div className='summary-header-filters-section'>
          <SelectedWellFilters
            filters={selectedFilters}
            onRemoveFilter={handleRemoveFilterUtil}
            recommendationsPage={true}
          />
        </div>
        {sliceLoading ? (
          <Loader />
        ) : (
          <GridLayout
            layout={layoutMemo}
            cols={12}
            rowHeight={30}
            isDraggable={false}
            isResizable={false}
            className='layout-react-grid'
            useCSSTransforms={false}
            isDroppable={false}
          >
            {layoutMemo.map((item) => (
              <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                {renderWidgetContent(item.i)}
              </div>
            ))}
          </GridLayout>
        )}
      </div>
    </ToastServiceProvider>
  );
};

export default ESPDashboard;
